import type { ComputedRef, Ref } from 'vue';

import type { SpacingDensities } from '../../../types/misc';

export enum DataViewVariant {
  Table = 'table',
}

export type DataViewVariants = `${DataViewVariant}`;

/**
 * A unique id for a sorting strategy. If the order is descending, it must begin with "-" (a hyphen/dash).
 *
 * Ex: `'product_name'` if ascending or `'-product_name'` if descending
 */
export type SortValue = string;

/**
 * The SortValue with its leading hyphen/dash removed
 */
export type SortId = string;

/**
 * Determined by whether or not there is a leading hyphen/dash in the SortValue
 */
export type SortOrder = 'asc' | 'desc';

/**
 * A deserialized instance of a `SortValue`.
 */
export type SortValueDeserialized = {
  id: SortId;
  order: SortOrder;
};

/** Updates the DataView internal state with the given sortId */
export type UpdateCurrentSort = (
  sortId: SortId | undefined,
  options?: {
    sortOrder?: SortOrder;
    /** If true, emits the `update:currentSort` and `update` events from DataView */
    shouldEmit?: boolean;
  },
) => void;

/** Updates the DataView internal state with the given page */
export type SetPage = (
  page: number,
  options?: {
    /** If true, emits the `update:currentPage` and `update` events from DataView */
    shouldEmit?: boolean;
  },
) => void;

/** Updates the DataView internal state with the previous page */
export type GoPrevPage = (options?: {
  /** If true, emits the `update:currentPage` and `update` events from DataView */
  shouldEmit?: boolean;
}) => void;

/** Updates the DataView internal state with the next page */
export type GoNextPage = (options?: {
  /** If true, emits the `update:currentPage` and `update` events from DataView */
  shouldEmit?: boolean;
}) => void;

/** Updates the DataView internal state with the given searchTerm */
export type UpdateCurrentSearch = (
  searchTerm?: string,
  options?: {
    /** If true, emits the `update:currentSearch` and `update` events from DataView */
    shouldEmit?: boolean;
  },
) => void;

/** Updates the DataView internal state with the given filter values */
export type UpdateCurrentFilters<FilterValues = object> = (
  newFilters: FilterValues,
  options?: {
    /** If true, emits the `update:currentSearch` and `update` events from DataView */
    shouldEmit?: boolean;
  },
) => void;

export interface UpdateEvent<FilterValues = object> {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  filters?: FilterValues;
}

export interface DataViewInjection {
  currentPage: ComputedRef<number>;

  /** Optional override for the next button disabled state  */
  isPaginateNextDisabled: ComputedRef<boolean>;

  isPaginationEnabled: ComputedRef<boolean>;

  pageCount: ComputedRef<number>;

  pageSize: ComputedRef<number>;

  totalDataCount: ComputedRef<number>;

  /** Updates the DataView internal state with the given page */
  setPage: SetPage;

  /** Updates the DataView internal state with the previous page */
  goPrevPage: GoPrevPage;

  /** Updates the DataView internal state with the next page */
  goNextPage: GoNextPage;

  currentSortId: ComputedRef<SortId | undefined>;

  currentSortOrder: ComputedRef<SortOrder | undefined>;

  /** Updates the DataView internal state with the given sortId */
  updateCurrentSort: UpdateCurrentSort;

  currentSearch: ComputedRef<string>;

  /** Updates the DataView internal state with the given searchTerm */
  updateCurrentSearch: UpdateCurrentSearch;

  /**
   * The DataView's padding; the default value is "comfortable". On small screens, the density will always be "compact".
   */
  density: ComputedRef<SpacingDensities | undefined>;

  /**
   * Defines if a DataView has a Toolbar rendered. This prop is updated when DataViewToolbar exists and is mounted.
   */
  hasToolbar?: Ref<boolean>;

  /**
   * Enables certain loading indicators to be displayed within the DataView.
   */
  isLoading?: ComputedRef<boolean>;

  /**
   * Enables certain empty states to be displayed within the DataView.
   */
  isEmpty?: ComputedRef<boolean>;

  /**
   * Allows Table to control whether or not a DataViewToolbar should show a checkmark
   */
  isSelectable?: Ref<boolean>;

  /**
   * Whether or not the DataView is rendered within a Module; the value is inferred based on being
   * able to inject details from Module.
   *
   * Note: This is sent to DataView child components so they only need to worry about interfacing
   * with the DataViewInjection API.
   */
  isWithinModule: ComputedRef<boolean>;

  /**
   * The DataView's variant; the default value is undefined.
   */
  variant: ComputedRef<DataViewVariants | undefined>;
}
